import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Palette, Plus, TrendingUp, Clock, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { designerAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';

const DesignerPortal = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [designs, setDesigns] = useState([]);
  const [stats, setStats] = useState(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role === 'designer') {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [designsRes, statsRes] = await Promise.all([
        designerAPI.getDesigns(),
        designerAPI.getStats()
      ]);
      setDesigns(designsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching designer data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!user || user.role !== 'designer') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-20 text-center">
          <Palette className="h-24 w-24 text-gray-300 mx-auto mb-6" />
          <h1 className="text-3xl font-bold mb-4">Designer Portal Access Required</h1>
          <p className="text-gray-600 mb-8">You need to register as a designer to access this portal.</p>
          <Button onClick={() => navigate('/role-selection')} className="bg-red-600 hover:bg-red-700">
            Register as Designer
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  if (user.status === 'pending') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-20">
          <Card className="max-w-2xl mx-auto p-8 text-center">
            <Clock className="h-24 w-24 text-orange-500 mx-auto mb-6" />
            <h1 className="text-3xl font-bold mb-4">Application Pending</h1>
            <p className="text-gray-600 mb-6">
              Your designer application is under review. You'll receive an email once approved by our admin team.
            </p>
            <p className="text-sm text-gray-500">Thank you for your patience!</p>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">Designer Portal</h1>
            <p className="text-gray-600">Create, submit, and track your designs</p>
          </div>
          <Button onClick={() => setShowSubmitModal(true)} className="bg-purple-600 hover:bg-purple-700">
            <Plus className="mr-2 h-5 w-5" />
            Submit New Design
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            <Card className="p-6">
              <p className="text-gray-600 text-sm">Total Submissions</p>
              <p className="text-3xl font-bold">{stats.total_submissions}</p>
            </Card>
            <Card className="p-6 bg-green-50 border-green-200">
              <p className="text-gray-600 text-sm">Approved</p>
              <p className="text-3xl font-bold text-green-700">{stats.approved}</p>
            </Card>
            <Card className="p-6 bg-orange-50 border-orange-200">
              <p className="text-gray-600 text-sm">Pending</p>
              <p className="text-3xl font-bold text-orange-700">{stats.pending}</p>
            </Card>
            <Card className="p-6">
              <p className="text-gray-600 text-sm">Approval Rate</p>
              <p className="text-3xl font-bold text-red-700">{stats.approval_rate}%</p>
            </Card>
          </div>
        )}

        {/* Designs List */}
        <Card className="p-6">
          <h2 className="text-2xl font-bold mb-6">Your Designs</h2>
          {designs.length === 0 ? (
            <div className="text-center py-12">
              <Palette className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">No designs submitted yet</p>
              <Button onClick={() => setShowSubmitModal(true)} className="bg-purple-600 hover:bg-purple-700">
                Submit Your First Design
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {designs.map((design) => (
                <div key={design.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-bold text-lg">{design.design_name}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          design.status === 'approved' ? 'bg-green-100 text-green-700' :
                          design.status === 'pending' ? 'bg-orange-100 text-orange-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {design.status === 'approved' && <CheckCircle className="inline h-3 w-3 mr-1" />}
                          {design.status === 'pending' && <Clock className="inline h-3 w-3 mr-1" />}
                          {design.status === 'rejected' && <XCircle className="inline h-3 w-3 mr-1" />}
                          {design.status.charAt(0).toUpperCase() + design.status.slice(1)}
                        </span>
                      </div>
                      <p className="text-gray-600 text-sm mb-2">{design.description}</p>
                      <div className="flex gap-4 text-sm text-gray-500">
                        <span>Category: {design.category}</span>
                        <span>Est. Cost: ₹{design.estimated_cost}</span>
                        <span>Target Price: ₹{design.target_price}</span>
                      </div>
                      {design.admin_notes && (
                        <div className="mt-3 p-3 bg-gray-100 rounded text-sm">
                          <strong>Admin Notes:</strong> {design.admin_notes}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {showSubmitModal && <DesignSubmitModal onClose={() => setShowSubmitModal(false)} onSuccess={fetchData} />}
      <Footer />
    </div>
  );
};

const DesignSubmitModal = ({ onClose, onSuccess }) => {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    design_name: '',
    description: '',
    category: '',
    images: '',
    estimated_cost: '',
    target_price: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const data = {
        ...formData,
        images: formData.images.split(',').map(url => url.trim()),
        estimated_cost: parseInt(formData.estimated_cost),
        target_price: parseInt(formData.target_price)
      };

      await designerAPI.submitDesign(data);
      
      toast({
        title: "Design Submitted!",
        description: "Your design is pending admin review.",
      });
      
      onSuccess();
      onClose();
    } catch (error) {
      toast({
        title: "Submission Failed",
        description: error.response?.data?.detail || "Please try again.",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
        <h2 className="text-2xl font-bold mb-6">Submit New Design</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Design Name *</Label>
            <Input
              value={formData.design_name}
              onChange={(e) => setFormData({...formData, design_name: e.target.value})}
              required
            />
          </div>

          <div>
            <Label>Description *</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              required
              rows={3}
            />
          </div>

          <div>
            <Label>Category *</Label>
            <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
              <SelectTrigger>
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Electronics">Electronics</SelectItem>
                <SelectItem value="Apparel">Apparel</SelectItem>
                <SelectItem value="Home Furnishings">Home Furnishings</SelectItem>
                <SelectItem value="Furniture">Furniture</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Image URLs (comma separated) *</Label>
            <Input
              value={formData.images}
              onChange={(e) => setFormData({...formData, images: e.target.value})}
              required
              placeholder="https://example.com/image1.jpg, https://example.com/image2.jpg"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Estimated Cost (₹) *</Label>
              <Input
                type="number"
                value={formData.estimated_cost}
                onChange={(e) => setFormData({...formData, estimated_cost: e.target.value})}
                required
              />
            </div>
            <div>
              <Label>Target Price (₹) *</Label>
              <Input
                type="number"
                value={formData.target_price}
                onChange={(e) => setFormData({...formData, target_price: e.target.value})}
                required
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} className="flex-1 bg-purple-600 hover:bg-purple-700">
              {submitting ? 'Submitting...' : 'Submit Design'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default DesignerPortal;
